import { CsvDataHelper } from '@domain/interface/data-map';
import { CSV_HEADERS } from '@domain/constants/csv-headers';
import {
	ACCEPTED_DATE_FORMATS,
	GET_MISSING_FIELDS,
	IS_EMPTY_LINE,
	MAP_HEADERS,
	MAP_INDICES,
} from '../shared/utils';
import {
	DIFFERENCE_IN_DAYS,
	IS_EXISTING_DATE,
	IS_NOT_EMPTY,
	IS_VALID_DATE,
} from 'cargo-chief-commons';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { environment } from '@environment/environment';

export const LOAD_DATA_MAP = (formBuilder: UntypedFormBuilder): CsvDataHelper => ({
	headersMap: [
		{ value: CSV_HEADERS.CR_NAME, label: 'CR Name', required: true },
		{ value: CSV_HEADERS.CR_EMAIL, label: 'CR Email', required: false },
		{ value: CSV_HEADERS.LOAD_NUMBER, label: 'Load Number', required: true },
		{ value: CSV_HEADERS.ORIGIN_CITY, label: 'Origin City', required: true },
		{ value: CSV_HEADERS.ORIGIN_STATE, label: 'Origin State', required: true },
		{ value: CSV_HEADERS.DESTINATION_CITY, label: 'Destination City', required: true },
		{ value: CSV_HEADERS.DESTINATION_STATE, label: 'Destination State', required: true },
		{ value: CSV_HEADERS.TRAILER_TYPE, label: 'Trailer Type', required: true },
		{ value: CSV_HEADERS.PICKUP_DATE, label: 'Pick up Date', required: true },
		{ value: CSV_HEADERS.DELIVERY_DATE, label: 'Delivery Date', required: false },
		{ value: CSV_HEADERS.RATE, label: 'Rate', required: false },
	],

	requiredHeaders: [
		CSV_HEADERS.CR_NAME,
		CSV_HEADERS.LOAD_NUMBER,
		CSV_HEADERS.ORIGIN_CITY,
		CSV_HEADERS.ORIGIN_STATE,
		CSV_HEADERS.DESTINATION_CITY,
		CSV_HEADERS.DESTINATION_STATE,
		CSV_HEADERS.TRAILER_TYPE,
		CSV_HEADERS.PICKUP_DATE,
	],

	fileDataValidator: (
		fileHeaders: string,
		fileData: string[],
		requiredHeaders: string[]
	): string[] => {
		let errors = [];
		let headers = fileHeaders.split(',');
		let headerMap = MAP_HEADERS(headers);
		let indicesMap = MAP_INDICES(headers);

		if (fileData.length > 0) {
			for (var i = 0; i < fileData.length; i++) {
				var line = fileData[i];
				if (IS_EMPTY_LINE(line)) {
					continue;
				}

				let missingFields = GET_MISSING_FIELDS(line, indicesMap, requiredHeaders);

				if (missingFields.length == 0) {
					var rowElements = line.split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/);

					var pickupDateValue = rowElements[headerMap['pick up date']];

					var deliveryDateValue = rowElements[headerMap['delivery date']];

					if (!IS_EXISTING_DATE(pickupDateValue, ACCEPTED_DATE_FORMATS)) {
						errors.push('Pickup date ' + pickupDateValue + ' does not exist');
					}

					if (
						IS_NOT_EMPTY(deliveryDateValue) &&
						!IS_EXISTING_DATE(deliveryDateValue, ACCEPTED_DATE_FORMATS)
					) {
						errors.push('Delivery date ' + deliveryDateValue + ' does not exist');
					}

					if (errors.length > 0) {
						return errors;
					}

					if (!IS_VALID_DATE(pickupDateValue, ACCEPTED_DATE_FORMATS)) {
						errors.push(
							'Invalid date format for pick up date: ' +
								pickupDateValue +
								'. Accepted formats: ' +
								ACCEPTED_DATE_FORMATS.join(', ')
						);
					}

					if (DIFFERENCE_IN_DAYS(new Date(), pickupDateValue) < 1) {
						errors.push('Pickup date cannot be less than current day.');
					}

					if (
						IS_NOT_EMPTY(deliveryDateValue) &&
						!IS_VALID_DATE(deliveryDateValue, ACCEPTED_DATE_FORMATS)
					) {
						errors.push(
							'Invalid date format for delivery date: ' +
								deliveryDateValue +
								'. Accepted formats: ' +
								ACCEPTED_DATE_FORMATS.join(', ')
						);
					}
					if (
						IS_NOT_EMPTY(deliveryDateValue) &&
						DIFFERENCE_IN_DAYS(new Date(), deliveryDateValue) < 1
					) {
						errors.push('Delivery date cannot be less than current day.');
					}

					if (
						IS_NOT_EMPTY(deliveryDateValue) &&
						DIFFERENCE_IN_DAYS(pickupDateValue, deliveryDateValue) < 1
					) {
						errors.push('Delivery date cannot be before pickup.');
					}
				} else {
					errors.push('Line is missing data for the required fields: ' + missingFields);
					errors.push('Data: ' + line);
					break;
				}
			}
		} else {
			errors.push('No record found.');
		}

		return errors;
	},

	formGroupBuilder: (): UntypedFormGroup => {
		let form: UntypedFormGroup;
		form = formBuilder.group({
			[CSV_HEADERS.CR_NAME]: ['', Validators.required],
			[CSV_HEADERS.CR_EMAIL]: ['', Validators.required],
			[CSV_HEADERS.LOAD_NUMBER]: ['', Validators.required],
			[CSV_HEADERS.ORIGIN_CITY]: ['', Validators.required],
			[CSV_HEADERS.ORIGIN_STATE]: ['', Validators.required],
			[CSV_HEADERS.DESTINATION_CITY]: ['', Validators.required],
			[CSV_HEADERS.DESTINATION_STATE]: ['', Validators.required],
			[CSV_HEADERS.TRAILER_TYPE]: ['', Validators.required],
			[CSV_HEADERS.PICKUP_DATE]: ['', Validators.required],
			[CSV_HEADERS.DELIVERY_DATE]: ['', Validators.required],
			[CSV_HEADERS.RATE]: ['', Validators.required],
		});
		return form;
	},

	fileUploadBucketName: environment.BULK_MATCH_CONFIG.load.bucketName,
});
